/**
 * What a sent message says about its delivery (mission stream contract phase
 * 4, agion/Agion#2327): one line per state of the generated DeliveryState
 * table, and for a failed or refused send, why and where it went. Since
 * phase 6 a refused receipt also names its typed reason
 * (receiptReasonTexts.ts).
 *
 * deliveryNote() switches over every state and ends in unknownKind(), so a
 * state added to the Go table fails tsc here until it has its text
 * (deliveryTexts.test.ts proves it).
 */

import {
  QUESTION_ANSWER_REASON_TEXTS,
  QUESTION_ANSWER_UNCONFIRMED_TEXT,
  QUESTION_CLOSED_TEXTS,
  isQuestionAnswerReason,
  unknownKind,
  type QuestionAnswerReason,
  type QuestionClosedReason,
  type ReceiptReason,
} from '@agion/mission-stream'
import type { AnswerOrigin, DeliveryFailure, MessageDelivery } from '../../hooks/useAnswerDelivery'
import { hasReasonText, receiptReasonText } from './receiptReasonTexts'

interface DeliveryCopy {
  /** i18n key of the text */
  key: string
  /** English fallback when the key is missing */
  fallback: string
}

export const DELIVERY_TEXTS = {
  waiting: {
    key: 'chat:delivery.waiting',
    fallback: 'Waiting for the files to finish uploading…',
  },
  sending: {
    key: 'chat:delivery.sending',
    fallback: 'Sending…',
  },
  queued: {
    key: 'chat:delivery.queued',
    fallback: 'Queued: the assistant reads it after its current step.',
  },
  delivered: {
    key: 'chat:delivery.delivered',
    fallback: 'Delivered',
  },
  notConnected: {
    key: 'chat:delivery.notConnected',
    fallback: 'Not sent: the connection to the assistant is down.',
  },
  noReceipt: {
    key: 'chat:delivery.noReceipt',
    fallback: 'Not confirmed: no receipt arrived, so the assistant may not have it.',
  },
  notStarted: {
    key: 'chat:delivery.notStarted',
    fallback: 'Not sent: the conversation could not start.',
  },
  notAccepted: {
    key: 'chat:delivery.notAccepted',
    fallback: 'The host did not confirm the message. Try again. If it keeps failing, the host may be offline or need an update.',
  },
  undelivered: {
    key: 'chat:delivery.undelivered',
    fallback: 'Not delivered: the run ended before the assistant read it.',
  },
  // The host's answer result with outcome unknown (agion/Agion#2767).
  unconfirmed: {
    key: 'chat:delivery.unconfirmed',
    fallback: QUESTION_ANSWER_UNCONFIRMED_TEXT.fallback,
  },
  refused: {
    key: 'chat:delivery.refused',
    fallback: 'Not delivered.',
  },
  backInComposer: {
    key: 'chat:delivery.backInComposer',
    fallback: 'It is back in the message box to send again.',
  },
  chooseAgain: {
    key: 'chat:delivery.chooseAgain',
    fallback: 'Choose your answer again.',
  },
} as const satisfies Record<string, DeliveryCopy>

export type DeliveryText = keyof typeof DELIVERY_TEXTS

/** progress: on its way; done: delivered; problem: failed or refused. */
export type DeliveryTone = 'progress' | 'done' | 'problem'

/** Read in order: text, detail, because, next. */
export interface DeliveryNote {
  tone: DeliveryTone
  /** What happened to the send. */
  text: DeliveryText
  /** The reason the relay or the server gave, shown as a sentence. */
  detail?: string
  /** The typed reason of a refused receipt (receiptReasonTexts.ts). */
  because?: ReceiptReason
  /** The typed reason the host refused an answer with (QUESTION_ANSWER_REASON_TEXTS). */
  answerBecause?: QuestionAnswerReason
  /** Where a returned send went. */
  next?: DeliveryText
}

function failureText(failure: DeliveryFailure | undefined): DeliveryText {
  switch (failure) {
    case 'not_connected':
      return 'notConnected'
    case 'not_started':
      return 'notStarted'
    case 'not_accepted':
      return 'notAccepted'
    case 'undelivered':
      return 'undelivered'
    case 'unconfirmed':
      return 'unconfirmed'
    case 'no_receipt':
    case undefined:
      return 'noReceipt'
    default:
      return unknownKind(failure, 'noReceipt')
  }
}

/**
 * A reason as a sentence, so it reads between the texts around it:
 * trimmed, its first character upper-cased, and a full stop added unless it
 * already ends in . ! ? or …. orchestration-service launch refusals are
 * lowercase fragments ("no agent host selected"); the relay's are sentences
 * and pass unchanged. The delivery line and the start alert both use it.
 */
export function asSentence(reason: string): string {
  const text = reason.trim().replace(/^./u, (first) => first.toUpperCase())
  return text === '' || /[.!?…]$/u.test(text) ? text : `${text}.`
}

/** What a message with this delivery says. `origin` names where a returned send went. */
export function deliveryNote(delivery: MessageDelivery, origin: AnswerOrigin = 'composer'): DeliveryNote {
  const next: DeliveryText = origin === 'choice' ? 'chooseAgain' : 'backInComposer'
  const reason = asSentence(delivery.detail ?? '')
  const detail = reason ? { detail: reason } : {}
  const because = delivery.reason && hasReasonText(delivery.reason) ? { because: delivery.reason } : {}
  const answerBecause = delivery.answerReason && isQuestionAnswerReason(delivery.answerReason)
    ? { answerBecause: delivery.answerReason }
    : {}
  switch (delivery.state) {
    case 'waiting':
      return { tone: 'progress', text: 'waiting' }
    case 'sending':
      return { tone: 'progress', text: 'sending' }
    case 'queued':
      return { tone: 'progress', text: 'queued' }
    case 'delivered':
      return { tone: 'done', text: 'delivered' }
    case 'failed':
      return { tone: 'problem', text: failureText(delivery.failure), ...detail, next }
    case 'refused':
      return { tone: 'problem', text: 'refused', ...detail, ...because, ...answerBecause, next }
    default:
      return unknownKind(delivery.state, { tone: 'problem', text: 'noReceipt' })
  }
}

type Translate = (key: string, fallback: string) => string

/** The translated text. */
export function deliveryText(t: Translate, text: DeliveryText): string {
  const { key, fallback } = DELIVERY_TEXTS[text]
  return t(key, fallback)
}

/** The translated text of a reason the host refused an answer with (agion/Agion#2767). */
export function questionAnswerReasonText(t: Translate, reason: QuestionAnswerReason): string {
  const { key, fallback } = QUESTION_ANSWER_REASON_TEXTS[reason]
  return t(`chat:${key}`, fallback)
}

/** The translated text of why the host closed a question; without a reason, that it is no longer open. */
export function questionClosedText(t: Translate, reason?: QuestionClosedReason): string {
  if (!reason) return t('chat:composer.questionClosed', 'This question is no longer open.')
  const { key, fallback } = QUESTION_CLOSED_TEXTS[reason]
  return t(`chat:${key}`, fallback)
}

/**
 * The whole delivery line of a sent message, translated and read in order
 * (text, detail, because, next), with its tone. The current chat's
 * DeliveryStatus and the new chat's delivery line both draw it.
 */
export function deliveryLine(t: Translate, delivery: MessageDelivery, origin?: AnswerOrigin): { tone: DeliveryTone; line: string } {
  const note = deliveryNote(delivery, origin)
  const line = [
    deliveryText(t, note.text),
    note.detail,
    note.because && receiptReasonText(t, note.because),
    note.answerBecause && questionAnswerReasonText(t, note.answerBecause),
    note.next && deliveryText(t, note.next),
  ]
    .filter(Boolean)
    .join(' ')
  return { tone: note.tone, line }
}
